import React from 'react'
import styled, { css } from 'styled-components'
import {
  BaseResponsiveDropdownProps,
  Box,
  Checkbox,
  Dropdown,
  FlatButton,
  Input,
  InputWrapper,
  Separator,
  Tooltip,
  useResponsiveDropdown,
} from '@sketch/components'
import { useThemeContext } from '@sketch/global-styles'

import { ReactComponent as FilterIconSvg } from '@sketch/icons/3-lines-filter-24'
import { ReactComponent as ArrowClockwise } from '@sketch/icons/arrow-clockwise-17'

import { InboxSearchOptions, useInboxSearchContext } from './InboxSearchContext'
import { InboxHeaderButton } from './Inbox.styles'

type NotificationTypeKey =
  | 'comments'
  | 'replies'
  | 'mentions'
  | 'downloadableAssets'
  | 'starredUpdates'

const NOTIFICATION_TYPE_OPTIONS: { key: NotificationTypeKey; label: string }[] =
  [
    { key: 'comments', label: 'Comments' },
    { key: 'replies', label: 'Replies' },
    { key: 'mentions', label: 'Mentions' },
    { key: 'downloadableAssets', label: 'Downloadable Assets' },
    { key: 'starredUpdates', label: 'Starred Updates' },
  ]

const FilterIcon = styled(FilterIconSvg)`
  height: 24px;
  width: 24px;
`

const OptionsContent = styled.div`
  width: 340px;
  padding: 8px 0;
`

const ResetButton = styled(FlatButton)(
  ({ theme }) => css`
    color: ${theme.colors.foreground.secondary.D};
    font-size: ${theme.fontSizes.D};
    padding: 12px 8px;
    display: flex;
    align-items: center;
    position: absolute;
    right: 12px;
    top: 4px;
  `
)

const ResetIcon = styled(ArrowClockwise)`
  width: 12px;
  height: 12px;
  margin-right: 8px;
`

const DatesWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    color: ${theme.colors.foreground.secondary.C};
    font-size: ${theme.fontSizes.C};
    align-items: center;
    padding: 0 20px 12px;
    justify-content: space-between;

    ${InputWrapper} {
      width: 128px;
    }
  `
)

const DatePicker = styled(Input).attrs({ type: 'date' })<{
  isDarkMode: boolean
}>(
  ({ theme, isDarkMode }) => css`
    width: 100%;
    border-radius: ${theme.radii.large};
    border: none;
    background-color: ${theme.colors.background.tertiary.C};
    color: ${theme.colors.state.disabled};
    margin: 0;
    font-size: ${theme.fontSizes.D};

    ::-webkit-calendar-picker-indicator {
      filter: ${isDarkMode ? 'invert(50%)' : undefined};
    }
  `
)

const FilterDropdown = (_: BaseResponsiveDropdownProps) => {
  const { options, setOptions, resetAll } = useInboxSearchContext()
  const { isDarkMode } = useThemeContext()

  const toggle = (key: keyof InboxSearchOptions) => () =>
    setOptions({ ...options, [key]: !options[key] })

  const handleDateUpdate = (
    e: React.ChangeEvent<HTMLInputElement>,
    dateType: 'from' | 'to'
  ) => setOptions({ ...options, [dateType]: e.target.value })

  return (
    <OptionsContent>
      <Dropdown.Header>Only show</Dropdown.Header>
      <Box pl={5} pr={5} pb={4}>
        <ResetButton onClick={resetAll}>
          <ResetIcon />
          Reset
        </ResetButton>
        {NOTIFICATION_TYPE_OPTIONS.map(({ key, label }, index) => (
          <Checkbox
            key={key}
            autoFocus={index === 0}
            value={options[key] ? 1 : 0}
            name={key}
            checked={options[key]}
            label={label}
            onChange={toggle(key)}
          />
        ))}
      </Box>
      <Separator />
      <Dropdown.Header>Between</Dropdown.Header>
      <DatesWrapper>
        <DatePicker
          type="date"
          placeholder="From"
          aria-label="from"
          isDarkMode={!!isDarkMode}
          value={options.from}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleDateUpdate(e, 'from')
          }
          max={options.to || new Date().toLocaleDateString('en-CA')}
        />
        <span>and</span>
        <DatePicker
          type="date"
          placeholder="To"
          aria-label="to"
          isDarkMode={!!isDarkMode}
          value={options.to}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleDateUpdate(e, 'to')
          }
          min={options.from}
          max={new Date().toLocaleDateString('en-CA')}
        />
      </DatesWrapper>
    </OptionsContent>
  )
}

export const InboxFilterMenu = () => {
  const { isOptionsEmpty } = useInboxSearchContext()
  const [content, buttonProps, { visible }] = useResponsiveDropdown({
    dropdown: FilterDropdown,
    placement: 'bottom-start',
    usePortal: true,
  })

  const isActive = visible || !isOptionsEmpty

  return (
    <>
      <Tooltip
        content="Filter"
        placement="bottom"
        disabled={visible}
        disableWhenTouchDevice
      >
        <InboxHeaderButton
          {...buttonProps}
          aria-label="Filter"
          $isActive={isActive}
        >
          <FilterIcon />
        </InboxHeaderButton>
      </Tooltip>
      {content}
    </>
  )
}
